import { useCallback, useState } from 'react'
import { useLazyGetInventoryProductsForImportQuery } from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import { useAccountBranchIdsParams } from '@/core/hooks/useAccountBranchIdsParams.ts'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'

interface Division {
  id: number
  name: string
}

const useGetInventoryProductsForImport = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>([])
  const [divisions, setDivisions] = useState<Division[]>([])

  const { accountId, branchId } = useAccountBranchIdsParams()

  const [getInventoryProductsQuery] =
    useLazyGetInventoryProductsForImportQuery()

  const fetchProducts = useCallback(
    async (term?: string, divisionId?: number) => {
      setIsLoading(true)

      try {
        const data = await getInventoryProductsQuery({
          accountId,
          branchId,
          term: term || undefined,
          divisionId: divisionId || undefined,
          excludePlanned: true,
          excludeCustom: true,
        }).unwrap()

        const internalAccount = data?.internal?.accounts?.nodes?.[0]
        const managerAccount = data?.manager?.account
        const managerBranch = data?.manager?.branches?.[0]

        const accountDivisions =
          internalAccount?.divisions ?? managerAccount?.divisions ?? []
        setDivisions(
          accountDivisions
            .filter(
              (d): d is { id: number; name: string } =>
                d !== null && d !== undefined,
            )
            .map((d) => ({ id: d.id!, name: d.name! })),
        )

        const inventoryProducts =
          internalAccount?.branches?.[0]?.inventoryProducts ??
          managerBranch?.inventoryProducts ??
          []

        const mappedProducts: Product[] = inventoryProducts
          .filter(
            (p): p is NonNullable<typeof p> => p !== null && p !== undefined,
          )
          .map((p) => ({
            id: p.id!,
            code: p.product.code,
            name: p.product.name,
            photo: p.product.photo ?? null,
            price: p.price ?? 0,
            unit: p.unit ?? null,
            category: null,
            group: null,
            speciesIds: [],
            isInPlan: false,
            erpCatalogName: p.product.erpCatalogName,
            salableProductId: null,
          }))

        setProducts(mappedProducts)
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    },
    [accountId, branchId, getInventoryProductsQuery],
  )

  return {
    fetchProducts,
    products,
    divisions,
    isLoading,
  }
}

export { useGetInventoryProductsForImport }
